<template>
  <transition name="file-reader-modal" appear>
    <div
      v-if="value && file"
      class="file-reader-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="file-reader-modal-title"
      @mousedown.self="handleBackdropMousedown"
    >
      <div ref="cardRef" class="file-reader-modal__card" tabindex="-1">
        <header class="file-reader-modal__header">
          <div class="file-reader-modal__title-wrap">
            <svg
              class="file-reader-modal__file-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M14 2v6h6M9 13h6M9 17h6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div class="file-reader-modal__title-text">
              <h2 id="file-reader-modal-title" class="file-reader-modal__title">
                {{ file.name }}
              </h2>
              <span class="file-reader-modal__badge">{{ fileTypeLabel }}</span>
            </div>
          </div>
          <button
            type="button"
            class="file-reader-modal__close-btn"
            aria-label="Close file reader"
            @click="handleClose"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </header>

        <div class="file-reader-modal__body">
          <iframe
            v-if="canShowInline && previewKind === 'pdf'"
            class="file-reader-modal__frame"
            :src="file.url"
            :title="file.name"
          />
          <img
            v-else-if="canShowInline && previewKind === 'image'"
            class="file-reader-modal__image"
            :src="file.url"
            :alt="file.name"
          />
          <iframe
            v-else-if="canShowInline && previewKind === 'doc' && officeViewerSrc"
            class="file-reader-modal__frame"
            :src="officeViewerSrc"
            :title="file.name"
          />
          <iframe
            v-else-if="canShowInline && previewKind === 'docx' && useOfficeViewerFallback && officeViewerSrc"
            class="file-reader-modal__frame"
            :src="officeViewerSrc"
            :title="file.name"
          />
          <div
            v-else-if="canShowInline && previewKind === 'docx'"
            class="file-reader-modal__docx-wrap"
          >
            <p v-if="docxPreviewState === 'loading'" class="file-reader-modal__docx-status">
              Loading preview...
            </p>
            <p v-else-if="docxPreviewState === 'error'" class="file-reader-modal__docx-status">
              Couldn't render this document inline.
            </p>
            <div ref="docxStyleContainerRef" class="file-reader-modal__docx-styles" aria-hidden="true" />
            <div ref="docxContainerRef" class="file-reader-modal__docx" />
          </div>

          <div v-else class="file-reader-modal__fallback">
            <svg
              class="file-reader-modal__fallback-icon"
              width="36"
              height="36"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M14 2v6h6"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <p class="file-reader-modal__fallback-title">{{ fallbackTitle }}</p>
            <p class="file-reader-modal__fallback-message">{{ fallbackMessage }}</p>
            <a
              v-if="file.url"
              class="file-reader-modal__download"
              :href="file.url"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download file
            </a>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { renderAsync } from "docx-preview";

const OFFICE_VIEWER_BASE = "https://view.officeapps.live.com/op/embed.aspx?src=";

const FILE_TYPE_LABELS = {
  pdf: "PDF",
  doc: "DOC",
  docx: "DOCX",
  txt: "TXT",
  csv: "CSV",
  xlsx: "XLSX",
  xls: "XLS",
  png: "PNG",
  jpg: "JPG",
  jpeg: "JPEG",
  gif: "GIF",
  webp: "WEBP",
  svg: "SVG",
};

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];

const props = defineProps({
  // v-model: whether the reader is open.
  value: {
    type: Boolean,
    default: false,
  },
  // Selected file card: { id, name, type?, contentType?, url? }.
  file: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["input", "close"]);

const cardRef = ref(null);
const docxContainerRef = ref(null);
const docxStyleContainerRef = ref(null);
const docxPreviewState = ref("idle");
const useOfficeViewerFallback = ref(false);
let previouslyFocused = null;
let openedAt = 0;
let docxRenderRequestId = 0;

// ER-11428: single click opens the reader. The second click of a user's
// double-click can land on the backdrop while the modal is still appearing, so
// backdrop closes are ignored briefly after opening to avoid an open/close flicker.
const BACKDROP_CLOSE_IGNORE_MS = 500;

function getFileExtension(name) {
  const match = /\.([a-zA-Z0-9]+)$/.exec(name || "");
  return match ? match[1].toLowerCase() : "";
}

function isDocxType(rawTypeValue, extension) {
  return (
    rawTypeValue.includes("wordprocessingml") ||
    rawTypeValue === "docx" ||
    extension === "docx"
  );
}

function isDocType(rawTypeValue, extension) {
  return (
    rawTypeValue === "application/msword" ||
    rawTypeValue === "msword" ||
    extension === "doc"
  );
}

// Prefer the explicit type field, but tolerate both extension-style values
// ("pdf") and full MIME values ("application/pdf") already used in the app.
const rawType = computed(() =>
  String(props.file?.type || props.file?.contentType || "").toLowerCase()
);
const typeToken = computed(() => {
  const raw = rawType.value;
  if (!raw) return "";
  return raw.includes("/") ? raw.split("/").pop() : raw;
});
const fileExtension = computed(() => getFileExtension(props.file?.name));

const fileTypeLabel = computed(() => {
  if (isDocxType(rawType.value, fileExtension.value)) return "DOCX";
  if (isDocType(rawType.value, fileExtension.value)) return "DOC";
  const token = typeToken.value || fileExtension.value;
  return FILE_TYPE_LABELS[token] || (token || "FILE").toUpperCase();
});

const previewKind = computed(() => {
  if (!props.file) return null;
  if (
    rawType.value === "application/pdf" ||
    typeToken.value === "pdf" ||
    fileExtension.value === "pdf"
  ) {
    return "pdf";
  }
  if (
    rawType.value.startsWith("image/") ||
    IMAGE_EXTENSIONS.includes(fileExtension.value) ||
    typeToken.value === "svg"
  ) {
    return "image";
  }
  if (isDocxType(rawType.value, fileExtension.value)) {
    return "docx";
  }
  if (isDocType(rawType.value, fileExtension.value)) {
    return "doc";
  }
  return null;
});

const officeViewerSrc = computed(() => {
  if (!props.file?.url) return null;
  return `${OFFICE_VIEWER_BASE}${encodeURIComponent(props.file.url)}`;
});

// Inline rendering only works when the file object carries a content URL.
const canShowInline = computed(() => !!props.file?.url && previewKind.value !== null);

const fallbackTitle = computed(() => {
  if (!props.file?.url) return "Preview unavailable";
  return "This file type can't be previewed here";
});

const fallbackMessage = computed(() => {
  if (!props.file?.url) {
    return `We can't show a preview for ${props.file?.name || "this file"} right now.`;
  }
  return `${props.file?.name || "This file"} can't be rendered inline in the reader.`;
});

async function renderDocxPreview() {
  const requestId = ++docxRenderRequestId;
  const url = props.file?.url;

  docxPreviewState.value = "idle";
  useOfficeViewerFallback.value = false;

  if (!props.value || previewKind.value !== "docx" || !url) {
    return;
  }

  docxPreviewState.value = "loading";
  await nextTick();

  const container = docxContainerRef.value;
  if (!container || requestId !== docxRenderRequestId) {
    return;
  }

  container.innerHTML = "";
  if (docxStyleContainerRef.value) {
    docxStyleContainerRef.value.innerHTML = "";
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch document (${response.status})`);
    }

    const blob = await response.blob();
    if (requestId !== docxRenderRequestId) {
      return;
    }

    await renderAsync(blob, container, docxStyleContainerRef.value, {
      className: "docx",
      inWrapper: true,
    });

    if (requestId !== docxRenderRequestId) {
      return;
    }

    docxPreviewState.value = "ready";
  } catch {
    if (requestId !== docxRenderRequestId) {
      return;
    }

    container.innerHTML = "";
    if (docxStyleContainerRef.value) {
      docxStyleContainerRef.value.innerHTML = "";
    }
    useOfficeViewerFallback.value = true;
    docxPreviewState.value = "error";
  }
}

watch(
  () => [props.value, props.file?.url, previewKind.value],
  () => {
    renderDocxPreview();
  }
);

watch(
  () => props.value,
  (open) => {
    if (open) {
      openedAt = Date.now();
      previouslyFocused = document.activeElement;
      nextTick(() => {
        cardRef.value?.focus();
      });
    } else {
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus();
        previouslyFocused = null;
      }
    }
  }
);

function handleBackdropMousedown() {
  if (Date.now() - openedAt < BACKDROP_CLOSE_IGNORE_MS) return;
  handleClose();
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) {
    handleClose();
  }
}

function handleClose() {
  emit("input", false);
  emit("close");
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  renderDocxPreview();
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  if (previouslyFocused && typeof previouslyFocused.focus === "function") {
    previouslyFocused.focus();
    previouslyFocused = null;
  }
});
</script>

<style scoped>
.file-reader-modal-enter-active,
.file-reader-modal-leave-active {
  transition: opacity 180ms ease;
}

.file-reader-modal-enter-from,
.file-reader-modal-leave-to {
  opacity: 0;
}

.file-reader-modal-enter-active .file-reader-modal__card,
.file-reader-modal-leave-active .file-reader-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.file-reader-modal-enter-from .file-reader-modal__card,
.file-reader-modal-leave-to .file-reader-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .file-reader-modal-enter-active,
  .file-reader-modal-leave-active,
  .file-reader-modal-enter-active .file-reader-modal__card,
  .file-reader-modal-leave-active .file-reader-modal__card {
    transition: none;
  }
}

.file-reader-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.file-reader-modal__card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  max-height: calc(100vh - 48px);
  border-radius: 12px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow: 0 8px 8px -4px rgba(16, 24, 40, 0.03), 0 20px 24px -4px rgba(16, 24, 40, 0.08);
  overflow: hidden;
  outline: none;
}

.file-reader-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eaecf0;
  background: #fcfcfd;
  flex-shrink: 0;
}

.file-reader-modal__title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.file-reader-modal__file-icon {
  display: block;
  color: #7f56d9;
  flex-shrink: 0;
}

.file-reader-modal__title-text {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.file-reader-modal__title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #101828;
}

.file-reader-modal__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f2edff;
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #7f56d9;
  white-space: nowrap;
  flex-shrink: 0;
}

.file-reader-modal__close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
  flex-shrink: 0;
}

.file-reader-modal__close-btn:hover {
  background: #f2f4f7;
  color: #344054;
}

.file-reader-modal__close-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.file-reader-modal__body {
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex: 1;
  min-height: 0;
  background: #f5f5f5;
}

.file-reader-modal__frame {
  display: block;
  width: 100%;
  height: 70vh;
  border: none;
  background: #ffffff;
}

.file-reader-modal__image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  align-self: center;
}

.file-reader-modal__docx-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  overflow: auto;
  background: #ffffff;
}

.file-reader-modal__docx-styles {
  display: none;
}

.file-reader-modal__docx {
  width: 100%;
  min-height: 70vh;
  padding: 16px;
  box-sizing: border-box;
}

.file-reader-modal__docx :deep(.docx-wrapper) {
  margin: 0 auto;
  background: #ffffff;
}

.file-reader-modal__docx-status {
  margin: 0;
  padding: 24px;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 13px;
  color: #667085;
}

.file-reader-modal__fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 300px;
  padding: 40px 24px;
  text-align: center;
}

.file-reader-modal__fallback-icon {
  display: block;
  color: #98a2b3;
  margin-bottom: 4px;
}

.file-reader-modal__fallback-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}

.file-reader-modal__fallback-message {
  margin: 0;
  max-width: 420px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: #667085;
}

.file-reader-modal__download {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #ffffff;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
}

.file-reader-modal__download:hover {
  background: #f9fafb;
}

.file-reader-modal__download:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}
</style>
